import DeepProxy from 'proxy-deep'
import { untracked } from '@angular/core'
import {
	WritableSignal,
	SignalImplementation,
} from './signal-implementation.js'

export interface ProxySignal<T> extends WritableSignal<T> {
	[HasSignalProperties]: true
	forceUpdate(): void
	isInitialized: boolean
}

export const HasSignalProperties = Symbol('HasSignalProperties')
export const ProxyValue = Symbol('ProxyValue')

const signalsToUpdate = new WeakSet<WritableSignal<any>>()
const signalProxyMap = new WeakMap<any, any>()
const signalsForRootObj = new WeakMap<any, Set<ProxySignal<any>>>()

export const Signals: ReturnType<typeof createProxySignalFactory> = {
	signal: () => {
		throw new Error('signal not initialized')
	},
	computed: () => {
		throw new Error('computed not initialized')
	},
	proxySignal: () => {
		throw new Error('proxySignal not initialized')
	},
	proxySignalDeferred: () => {
		throw new Error('proxySignalDeferred not initialized')
	},
}
export function createProxySignalFactory(
	signalImplementation: SignalImplementation,
) {
	const functions = {
		signal: signalImplementation.signal,
		computed: signalImplementation.computed,

		proxySignal: function <T>(value: T): ProxySignal<T> {
			return functions.proxySignalDeferred(() => value)
		},

		proxySignalDeferred: function <T>(valueFn: () => T): ProxySignal<T> {
			const signalProxyResult = ensureSignalProxy(untracked(() => valueFn()))

			const underlyingSignal: WritableSignal<T> = signalImplementation!.signal(
				signalProxyResult.proxifiedObj,
				{
					equal: (a, b) => {
						if (a !== b) return false

						if (signalsToUpdate.has(underlyingSignal)) {
							signalsToUpdate.delete(underlyingSignal)
							return false
						}

						return true
					},
				},
			)

			const oldSetFn = underlyingSignal.set
			const newSignal = underlyingSignal as ProxySignal<T>
			newSignal[HasSignalProperties] = true
			newSignal.isInitialized = false
			newSignal.forceUpdate = () => {
				signalsToUpdate.add(underlyingSignal)
				underlyingSignal.set(underlyingSignal())
				newSignal.isInitialized = true
			}
			newSignal.set = (newValue: T) => {
				const signalProxyResult = ensureSignalProxy(newValue)
				oldSetFn.call(newSignal, signalProxyResult.proxifiedObj)
				signalProxyResult.registerSignal(newSignal)
				newSignal.isInitialized = true
			}

			signalProxyResult.registerSignal(newSignal)
			return newSignal
		},

		// proxySignalDeferred: function <T>(valueFn: () => T) {
		// 	const underlyingProxySignal = Signals.proxySignal<T>(undefined as any)
		// 	underlyingProxySignal.isInitialized = false

		// 	let isSignalValueOverriden = false
		// 	let signalValueOverride: any = undefined

		// 	const newSignal = function () {
		// 		if (!newSignal.isInitialized) {
		// 			// Important: this signal read could be happening from within a computed; therefore it is not allowed to set the signal value of the underlying signal here
		// 			// Instead, an overriden value is returned and will be set in the next microtask
		// 			if (!isSignalValueOverriden) {
		// 				isSignalValueOverriden = true
		// 				signalValueOverride = valueFn()
		// 				underlyingProxySignal() // Important: must read the signal to ensure dependencies are tracked!
		// 				queueMicrotask(() => {
		// 					if (isSignalValueOverriden) {
		// 						// value could have been changed already through a set call
		// 						newSignal.set(signalValueOverride)
		// 					}
		// 				})
		// 			}
		// 			return signalValueOverride
		// 		}
		// 		return underlyingProxySignal()
		// 	} as typeof underlyingProxySignal

		// 	;(newSignal as any)[Symbol.for('SIGNAL')] = true
		// 	newSignal[HasSignalProperties] =
		// 		underlyingProxySignal[HasSignalProperties]
		// 	newSignal.forceUpdate = underlyingProxySignal.forceUpdate
		// 	newSignal.set = (value: T) => {
		// 		underlyingProxySignal.set(value)
		// 		isSignalValueOverriden = false
		// 		signalValueOverride = undefined
		// 	}
		// 	newSignal.update = underlyingProxySignal.update
		// 	Object.defineProperty(newSignal, 'isInitialized', {
		// 		get: () => underlyingProxySignal.isInitialized,
		// 		set: (val) => (underlyingProxySignal.isInitialized = val),
		// 	})

		// 	// queueMicrotask(newSignal) // force reading to trigger the valueFn

		// 	return newSignal
		// },
	}

	Object.assign(Signals, functions)

	return functions
}

function shouldProxifyValue(value: any) {
	if (
		!value ||
		(typeof value != 'object' && typeof value != 'function') ||
		value[HasSignalProperties]
	)
		return false
	if (value instanceof Date) return false
	// if (
	// 	value instanceof Map ||
	// 	value instanceof Set ||
	// 	value instanceof WeakMap ||
	// 	value instanceof WeakSet
	// )
	// 	return false
	if (
		value instanceof ArrayBuffer ||
		value instanceof Uint8Array ||
		value instanceof DataView ||
		value instanceof Blob ||
		value instanceof File
	)
		return false
	return true
}

function ensureSignalProxy<T>(rootObj: T) {
	// result includes a registerSignal function to be used to register the newly created signal for which ensureSignalProxy is invoked.
	// This is necessary to because the signal does not yet exist at the time this function is called.
	const result = {
		proxifiedObj: rootObj,
		registerSignal: (_signal: ProxySignal<any>) => {},
	}

	if (!shouldProxifyValue(rootObj)) {
		return result
	}

	if (signalProxyMap.has(rootObj)) {
		result.proxifiedObj = signalProxyMap.get(rootObj)
		return result
	}

	const signalsForObj =
		signalsForRootObj.get(rootObj) ?? new Set<ProxySignal<any>>()
	signalsForRootObj.set(rootObj, signalsForObj)

	const signalProxy = new DeepProxy(rootObj as object, {
		get(target, prop, _receiver) {
			if (prop === HasSignalProperties) return true
			if (prop === ProxyValue) return target

			const value = Reflect.get(target, prop)
			if (!shouldProxifyValue(value) || prop == 'constructor') return value
			if (target instanceof Date) return value.bind(target)

			if (signalProxyMap.has(value)) return signalProxyMap.get(value)

			if (typeof value == 'function') {
				const doWrapWithSignalUpdater =
					(Array.isArray(target) &&
						[
							'push',
							'pop',
							'shift',
							'unshift',
							'splice',
							'sort',
							'reverse',
						].includes(prop as any)) ||
					((target instanceof Set || target instanceof WeakSet) &&
						['add', 'delete', 'clear'].includes(prop as any)) ||
					((target instanceof Map || target instanceof WeakMap) &&
						['set', 'delete', 'clear'].includes(prop as any))
				// do NOT wrap Date object with signal updater! This would break the Date internal functionality

				if (doWrapWithSignalUpdater) {
					return (...args: any[]) => {
						const retVal = (value as (...args: any[]) => any).apply(
							target,
							args,
						)

						const signals = signalsForRootObj.get(rootObj)
						signals?.forEach((signal) => signal.forceUpdate())

						return retVal
					}
				}
				return value.bind(target)
			}

			if (typeof value != 'object') return value

			const valueProxy = this.nest(value)
			signalProxyMap.set(value, valueProxy)
			return valueProxy
		},

		set(target, prop, value, receiver) {
			const retVal = Reflect.set(target, prop, value, receiver)
			signalsForRootObj.get(rootObj)?.forEach((signal) => signal.forceUpdate())
			return retVal
		},
	}) as T
	signalProxyMap.set(rootObj, signalProxy)

	result.proxifiedObj = signalProxy
	result.registerSignal = (signal: ProxySignal<any>) =>
		signalsForObj.add(signal)
	return result
}
